import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Building2, CheckCircle2 } from "lucide-react";
import client from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function ForEmployers() {
  const [form, setForm] = useState({
    company_name: "", contact_person: "", email: "", phone: "",
    country: "", position: "", workers: "", message: "",
  });
  const [countries, setCountries] = useState([]);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    client.get("/public/countries").then((r) => setCountries(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await client.post("/public/enquiry", { ...form, workers: Number(form.workers) || null });
      setSent(true);
      toast.success("Enquiry submitted — our team will contact you shortly.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit enquiry");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-12 items-start">
      <div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0088FF]/10 text-[#0088FF] text-xs font-medium mb-5">
          <Building2 className="h-4 w-4" /> For Employers &amp; Clients
        </div>
        <h1 className="font-display text-4xl font-extrabold text-[#0A192F]">Hire a workforce that's deployment-ready</h1>
        <p className="text-slate-600 mt-4 leading-relaxed">
          Submit your workforce requirement and our team will convert it into a sourcing project. We handle
          screening, trade testing, medical, visa processing and deployment logistics end-to-end.
        </p>
        <ul className="mt-6 space-y-3">
          {["Pre-screened, certified candidate pool", "GAMCA medical & visa processing managed", "Minimum ticket size 50 workers", "Dedicated project tracking"].map((t) => (
            <li key={t} className="flex items-center gap-2 text-slate-700"><CheckCircle2 className="h-5 w-5 text-emerald-500" /> {t}</li>
          ))}
        </ul>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
        {sent ? (
          <div className="text-center py-10">
            <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto" />
            <h3 className="font-display text-2xl font-bold text-[#0A192F] mt-4">Thank you!</h3>
            <p className="text-slate-500 mt-2">Your workforce requirement has been received. Our team will reach out shortly.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="employer-quote-form">
            <h3 className="font-display text-xl font-bold text-[#0A192F]">Request a Quote</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Company Name *</Label><Input required data-testid="quote-company" value={form.company_name} onChange={set("company_name")} /></div>
              <div><Label>Contact Person *</Label><Input required data-testid="quote-contact" value={form.contact_person} onChange={set("contact_person")} /></div>
              <div><Label>Email *</Label><Input required type="email" data-testid="quote-email" value={form.email} onChange={set("email")} /></div>
              <div><Label>Phone</Label><Input data-testid="quote-phone" value={form.phone} onChange={set("phone")} /></div>
              <div>
                <Label>Country</Label>
                <select data-testid="quote-country" value={form.country} onChange={set("country")} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                  <option value="">Select</option>
                  {countries.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              <div><Label>Position</Label><Input data-testid="quote-position" value={form.position} onChange={set("position")} placeholder="e.g. Electrician" /></div>
              <div><Label>No. of Workers</Label><Input type="number" data-testid="quote-workers" value={form.workers} onChange={set("workers")} /></div>
            </div>
            <div><Label>Requirement Details</Label><Textarea data-testid="quote-message" value={form.message} onChange={set("message")} rows={3} /></div>
            <Button type="submit" disabled={loading} data-testid="employer-quote-submit" className="w-full bg-[#0088FF] hover:bg-[#0070E0] h-11">
              {loading ? "Submitting..." : "Submit Requirement"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
